<script lang="ts">
  import { onMount, onDestroy, createEventDispatcher } from "svelte";
  import { shortcuts } from "./shortcuts";
  import { multiSelection } from "../store/multiSelection";
  import { shouldIgnoreKeyEvent } from "../../../js/utils/keyboard";
  import {
    Designer,
    DefinitionWalker,
    type Definition,
    type StepsConfiguration,
    type ToolboxConfiguration,
    type Step,
    type StepEditorContext,
    type RootEditorContext,
    type UndoStack,
    type ValidatorConfiguration,
    type UidGenerator,
    type DesignerExtension,
    type EditorsConfiguration,
    type CustomActionHandler,
    type StepEditorProvider,
    type RootEditorProvider,
    type KeyboardConfiguration,
    type I18n,
    type PreferenceStorage,
  } from "sequential-workflow-designer";

  const dispatch = createEventDispatcher<{
    definitionChanged: {
      definition: Definition;
      isValid: boolean;
    };
    selectedStepIdChanged: {
      stepId: string | null;
    };
    isToolboxCollapsedChanged: {
      isCollapsed: boolean;
    };
    isEditorCollapsedChanged: {
      isCollapsed: boolean;
    };
  }>();

  export let definition: Definition;
  export let steps: StepsConfiguration;
  export let toolbox: Omit<ToolboxConfiguration, "isCollapsed"> | false = false;
  export let isToolboxCollapsed = false;
  export let controlBar = true;
  export let theme = "light";
  export let contextMenu = true;
  export let keyboard: boolean | KeyboardConfiguration | undefined = undefined;
  export let preferenceStorage: PreferenceStorage | undefined = undefined;
  export let undoStackSize: number | undefined = undefined;
  export let undoStack: UndoStack | undefined = undefined;
  export let validator: ValidatorConfiguration | undefined = undefined;
  export let definitionWalker: DefinitionWalker | undefined = undefined;
  export let extensions: DesignerExtension[] | undefined = undefined;
  export let i18n: I18n | undefined = undefined;
  export let uidGenerator: UidGenerator | undefined = undefined;
  export let customActionHandler: CustomActionHandler | undefined = undefined;
  export let badgeStore = undefined; // NEW
  export let layoutStore = undefined; // NEW
  export let codegenErrorStore = undefined; // NEW
  export let stepWarningsStore = undefined;

  /**
   * @description Svelte component that will be used to render the step editor. If this property is set, the `nativeStepEditor` property will be ignored.
   */
  export let stepEditor: ConstructorOfATypedSvelteComponent | null = null;
  /**
   * @description Function that will be used to render the step editor.
   */
  export let nativeStepEditor: StepEditorProvider | null = null;
  /**
   * @description Svelte component that will be used to render the root editor. If this property is set, the `nativeRootEditor` property will be ignored.
   */
  export let rootEditor: ConstructorOfATypedSvelteComponent | null = null;
  /**
   * @description Function that will be used to render the root editor.
   */
  export let nativeRootEditor: RootEditorProvider | null = null;

  export let isEditorCollapsed = false;
  export let isReadonly = false;
  export let selectedStepId: string | null = null;

  let isFirstChange = true;
  let designer: Designer | null = null;
  let placeholder: HTMLElement;
  let unsubscribeBadgeStore = null;
  let unsubscribeLayoutStore = null;
  let unsubscribeCodegenErrorStore = null;
  let unsubscribeStepWarningsStore = null;
  let unsubscribeMultiSelection = null;
  let isShiftPressed = false;

  function updateMultiSelectionClasses(selectedIds) {
    if (!placeholder) return;

    // Remove class from all steps first - more efficient to query only the ones we know about or all?
    // Since we don't track DOM elements map, querying all with the class is safest
    placeholder.querySelectorAll(".sqd-step-selected-multi").forEach((el) => {
      el.classList.remove("sqd-step-selected-multi");
    });

    // Add class to selected steps
    selectedIds.forEach((id) => {
      // The library usually uses data-step-id attribute
      const stepElement = placeholder.querySelector(`[data-step-id="${id}"]`);
      if (stepElement) {
        stepElement.classList.add("sqd-step-selected-multi");
      }
    });
  }

  function init() {
    const editors: EditorsConfiguration | false =
      (stepEditor || nativeStepEditor) && (rootEditor || nativeRootEditor)
        ? {
            isCollapsed: isEditorCollapsed,
            stepEditorProvider: (
              step: Step,
              context: StepEditorContext,
              def: Definition,
              isReadonly: boolean,
            ) => {
              if (stepEditor) {
                const root = document.createElement("div");
                new stepEditor({
                  target: root,
                  props: { step, context, definition: def, isReadonly },
                });
                return root;
              }
              if (nativeStepEditor) {
                return nativeStepEditor(step, context, def, isReadonly);
              }
              throw new Error("No step editor provided");
            },
            rootEditorProvider: (
              def: Definition,
              context: RootEditorContext,
              isReadonly: boolean,
            ) => {
              if (rootEditor) {
                const root = document.createElement("div");
                new rootEditor({
                  target: root,
                  props: { definition: def, context, isReadonly },
                });
                return root;
              }
              if (nativeRootEditor) {
                return nativeRootEditor(def, context, isReadonly);
              }
              throw new Error("No root editor provided");
            },
          }
        : false;
    const _toolbox: ToolboxConfiguration | false = toolbox
      ? {
          ...toolbox,
          isCollapsed: isToolboxCollapsed,
        }
      : false;

    const d = Designer.create(placeholder, definition, {
      steps,
      controlBar,
      toolbox: _toolbox,
      editors,
      theme,
      contextMenu,
      keyboard,
      preferenceStorage,
      undoStackSize,
      undoStack,
      validator,
      definitionWalker,
      extensions,
      isReadonly,
      i18n,
      uidGenerator,
      customActionHandler,
    });

    d.onReady.subscribe((x) => {
      dispatch("definitionChanged", {
        definition: d.getDefinition(),
        isValid: d.isValid(),
      });

      unsubscribeLayoutStore = layoutStore.subscribe(() => {
        d.updateLayout();
        // Re-apply selection classes after layout update
        updateMultiSelectionClasses(multiSelection.getAll());
      });

      unsubscribeBadgeStore = badgeStore.subscribe(() => {
        try {
          d.updateBadges();
        } catch (e) {
          console.warn("Error updating badges:", e);
        }
      });

      if (codegenErrorStore) {
        unsubscribeCodegenErrorStore = codegenErrorStore.subscribe(() => {
          try {
            d.updateBadges();
          } catch (e) {
            console.warn("Error updating badges on codegen error change:", e);
          }
        });
      }

      if (stepWarningsStore) {
        unsubscribeStepWarningsStore = stepWarningsStore.subscribe(() => {
          try {
            d.updateBadges();
          } catch (e) {
            console.warn("Error updating badges on step warning change:", e);
          }
        });
      }

      unsubscribeMultiSelection = multiSelection.subscribe((ids) => {
        updateMultiSelectionClasses(ids);
      });
    });
    d.onDefinitionChanged.subscribe((event) =>
      dispatch("definitionChanged", {
        definition: event.definition,
        isValid: d.isValid(),
      }),
    );

    // Intercept selection changes
    d.onSelectedStepIdChanged.subscribe((stepId) => {
      if (stepId && isShiftPressed) {
        // Toggle in multi-selection
        multiSelection.toggle(stepId);

        // When toggling, we want to ensure the visual state reflects immediately
        // but we also rely on the store subscription to update visuals
      } else if (stepId) {
        // Normal click - clear multi selection unless we want to treat single selection as size-1 set
        multiSelection.clear();
        multiSelection.add(stepId);
      } else {
        // Clicked on empty space
        if (!isShiftPressed) {
          multiSelection.clear();
        }
      }
      dispatch("selectedStepIdChanged", { stepId });
    });

    d.onIsToolboxCollapsedChanged.subscribe((isCollapsed) =>
      dispatch("isToolboxCollapsedChanged", { isCollapsed }),
    );
    d.onIsEditorCollapsedChanged.subscribe((isCollapsed) =>
      dispatch("isEditorCollapsedChanged", { isCollapsed }),
    );

    if (selectedStepId) {
      d.selectStepById(selectedStepId);
    }

    return d;
  }

  onMount(() => {
    designer = init();

    const handleKeyDownGlobal = (e) => {
      if (e.key === "Shift") {
        isShiftPressed = true;
      }
    };

    const handleKeyUpGlobal = (e) => {
      if (e.key === "Shift") {
        isShiftPressed = false;
      }
    };

    window.addEventListener("keydown", handleKeyDownGlobal);
    window.addEventListener("keyup", handleKeyUpGlobal);

    return () => {
      window.removeEventListener("keydown", handleKeyDownGlobal);
      window.removeEventListener("keyup", handleKeyUpGlobal);
    };
  });

  $: {
    if (designer) {
      const isDefinitionChanged = !isFirstChange && definition !== designer.getDefinition();
      if (isDefinitionChanged) {
        designer.replaceDefinition(definition);
        // Re-apply classes after definition replacement
        setTimeout(() => updateMultiSelectionClasses(multiSelection.getAll()), 0);
      } else {
        isFirstChange = false;

        if (isReadonly !== designer.isReadonly()) {
          designer.setIsReadonly(isReadonly);
        }
        if (selectedStepId !== designer.getSelectedStepId()) {
          if (selectedStepId) {
            designer.selectStepById(selectedStepId);
          } else {
            designer.clearSelectedStep();
          }
        }
        if (isEditorCollapsed !== designer.isEditorCollapsed()) {
          designer.setIsEditorCollapsed(isEditorCollapsed);
        }
        if (isToolboxCollapsed !== designer.isToolboxCollapsed()) {
          designer.setIsToolboxCollapsed(isToolboxCollapsed);
        }
      }
    }
  }

  // The library only reads `theme` at create; follow later changes by swapping
  // the class it stamps on the root (and on any open drag/context-menu nodes).
  $: if (designer && placeholder) {
    for (const el of document.querySelectorAll(".sqd-theme-light, .sqd-theme-dark")) {
      el.classList.remove("sqd-theme-light", "sqd-theme-dark");
      el.classList.add(`sqd-theme-${theme}`);
    }
  }

  onDestroy(() => {
    if (designer) {
      if (unsubscribeBadgeStore) unsubscribeBadgeStore();
      if (unsubscribeLayoutStore) unsubscribeLayoutStore();
      if (unsubscribeCodegenErrorStore) unsubscribeCodegenErrorStore();
      if (unsubscribeStepWarningsStore) unsubscribeStepWarningsStore();
      if (unsubscribeMultiSelection) unsubscribeMultiSelection();
      designer.destroy();
      designer = null;
    }
  });

  function handleKeydown(e) {
    if (!shouldIgnoreKeyEvent(e)) {
      document.activeElement.blur();
      shortcuts.handleKeydown(e, designer, isReadonly);
    }
  }
</script>

<!-- https://github.com/nocode-js/sequential-workflow-designer/blob/main/svelte/src/lib/SequentialWorkflowDesigner.svelte -->

<svelte:window on:keydown={handleKeydown} />

<div bind:this={placeholder} class="sqd-designer-svelte" />
